import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { exportCourseScoresReport, exportCourseScoresExcel } from '../../utils/exportUtils';
import { calculateWeightedFinalGrade, formatGradeOneDecimal, floorToOneDecimal, normalizeExamType } from '../../utils/gradeCalculation';
import { getStudentCode } from '../../utils/studentCode';
import './Reports.css';

// Familienaam (uit het studentprofiel) is leidend voor sortering en weergave: "Familienaam Voornaam".
const familyNameOf = (student) => String(student?.studentProfile?.familyName || student?.lastName || '').trim();
const displayStudentName = (student) => {
  const family = familyNameOf(student);
  const first = String(student?.firstName || '').trim();
  return [family, first].filter(Boolean).join(' ') || student?.name || '-';
};
const compareByFamilyName = (a, b) => {
  const byFamily = (familyNameOf(a) || a?.name || '').localeCompare(familyNameOf(b) || b?.name || '', 'nl', { sensitivity: 'base' });
  if (byFamily !== 0) return byFamily;
  return String(a?.firstName || '').localeCompare(String(b?.firstName || ''), 'nl', { sensitivity: 'base' });
};

const ReportGrades = () => {
  const { t } = useTranslation();
  const { selectedLocation } = useOutletContext();
  const { user } = useAuth();
  const { data, upsertGrade } = useData();

  const [selectedStudy, setSelectedStudy] = useState('');
  const [selectedStudyYear, setSelectedStudyYear] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedExamPoint, setSelectedExamPoint] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [gradeDrafts, setGradeDrafts] = useState({});
  const [savingGradeKey, setSavingGradeKey] = useState('');

  // All courses visible to this user/location
  const userCourses = useMemo(() => {
    let courses = data.courses || [];
    if (user.role === 'instructor') {
      const userId = String(user.id || user._id);
      courses = courses.filter(
        (c) => String(c.instructor?.id || c.instructor?._id || c.instructor || '') === userId
      );
    }
    if (user.role !== 'admin' && user.location) {
      courses = courses.filter((c) => c.location === user.location);
    }
    if (user.role === 'admin' && selectedLocation && selectedLocation !== 'all') {
      courses = courses.filter((c) => c.location === selectedLocation);
    }
    return courses;
  }, [data.courses, user, selectedLocation]);

  const yearToStudyMap = useMemo(() => {
    const map = new Map();
    (data.studies || []).forEach((study) => {
      const studyId = String(study.id || study._id);
      (study.studyYears || []).forEach((year) => {
        map.set(String(year.id || year._id), studyId);
      });
    });
    return map;
  }, [data.studies]);

  const studiesWithCourses = useMemo(() => {
    return (data.studies || []).filter((study) => {
      const yearIds = new Set((study.studyYears || []).map((y) => String(y.id || y._id)));
      return userCourses.some((course) => {
        const courseYearId = String(course.studyYear?.id || course.studyYear?._id || course.studyYear || '');
        if (courseYearId && yearIds.has(courseYearId)) return true;
        const moduleIds = new Set((course.modules || []).map((m) => String(m.id || m._id || m)));
        const singleModuleId = String(course.module?.id || course.module?._id || course.module || '');
        return (study.studyYears || []).some((year) =>
          (year.modules || []).some((module) => {
            const moduleId = String(module.id || module._id);
            return moduleId === singleModuleId || moduleIds.has(moduleId);
          })
        );
      });
    });
  }, [data.studies, userCourses]);

  const availableStudyYears = useMemo(() => {
    if (!selectedStudy) return [];
    const study = (data.studies || []).find((s) => String(s.id || s._id) === String(selectedStudy));
    return (study?.studyYears || []).slice().sort((a, b) => Number(a.yearNumber || 0) - Number(b.yearNumber || 0));
  }, [data.studies, selectedStudy]);

  const availableModules = useMemo(() => {
    const modules = [];
    availableStudyYears.forEach((year) => {
      if (selectedStudyYear && String(year.id || year._id) !== String(selectedStudyYear)) return;
      (year.modules || []).forEach((module) => modules.push(module));
    });
    return modules.sort((a, b) => (a.code || a.name || '').localeCompare(b.code || b.name || '', 'nl'));
  }, [availableStudyYears, selectedStudyYear]);

  // Filter courses by study, study year and module (fallbacks on module relation when studyYear is missing)
  const filteredCoursesByStudy = useMemo(() => {
    return userCourses.filter((course) => {
      if (selectedModule) {
        const single = String(course.module?.id || course.module?._id || course.module || '');
        const multi = (course.modules || []).map((m) => String(m.id || m._id || m));
        if (single !== String(selectedModule) && !multi.includes(String(selectedModule))) return false;
      }

      if (!selectedStudy && !selectedStudyYear) return true;

      const courseYearId = String(course.studyYear?.id || course.studyYear?._id || course.studyYear || '');
      if (selectedStudyYear && courseYearId && courseYearId !== String(selectedStudyYear)) {
        return false;
      }

      if (selectedStudy) {
        if (courseYearId) {
          const parentStudyId = yearToStudyMap.get(courseYearId);
          if (parentStudyId === String(selectedStudy)) {
            return true;
          }
        }

        const study = (data.studies || []).find((s) => String(s.id || s._id) === String(selectedStudy));
        if (!study) return false;
        const allowedModules = new Set();
        (study.studyYears || []).forEach((year) => {
          if (selectedStudyYear && String(year.id || year._id) !== String(selectedStudyYear)) return;
          (year.modules || []).forEach((module) => allowedModules.add(String(module.id || module._id)));
        });

        if (allowedModules.size === 0) return false;
        const single = String(course.module?.id || course.module?._id || course.module || '');
        const multi = (course.modules || []).map((m) => String(m.id || m._id || m));
        return allowedModules.has(single) || multi.some((id) => allowedModules.has(id));
      }

      if (selectedStudyYear) {
        return courseYearId === String(selectedStudyYear);
      }

      return true;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nl'));
  }, [data.studies, selectedModule, selectedStudy, selectedStudyYear, userCourses, yearToStudyMap]);

  const selectedCourseDoc = useMemo(
    () => (data.courses || []).find((c) => String(c.id || c._id) === String(selectedCourse)),
    [data.courses, selectedCourse]
  );

  const scoreExamPoints = useMemo(() => {
    const configuredExamPoints = Array.isArray(selectedCourseDoc?.examPoints)
      ? selectedCourseDoc.examPoints
      : [];

    if (configuredExamPoints.length > 0) {
      return configuredExamPoints.map((point, index) => {
        const examType = normalizeExamType(point.name || 'other');
        const pointKey = String(point._id || point.id || `${examType}-${index}`);
        return {
          ...point,
          pointKey,
          examType,
          displayName: point.name || `Onderdeel ${index + 1}`
        };
      });
    }

    // Fallback for historical data: derive visible exam points from stored grades.
    const selectedCourseId = String(selectedCourseDoc?.id || selectedCourseDoc?._id || '');
    if (!selectedCourseId) return [];

    const pointMap = new Map();
    (data.grades || [])
      .filter((grade) => String(grade.courseId) === selectedCourseId)
      .forEach((grade, index) => {
        const examType = normalizeExamType(grade.examType || 'other');
        const pointKey = String(grade.examPointKey || `${examType}-legacy-${index}`);
        if (pointMap.has(pointKey)) return;

        pointMap.set(pointKey, {
          pointKey,
          examType,
          weight: Number(grade.weight || 0),
          displayName: String(grade.notes || grade.examType || `Onderdeel ${index + 1}`)
        });
      });

    return Array.from(pointMap.values());
  }, [data.grades, selectedCourseDoc]);

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} ${cohortNames.join(', ')}` : study.name;
  };

  const getDraftValue = (studentId, courseId, examPointKey) => {
    const key = `${studentId}-${courseId}-${examPointKey}`;
    if (gradeDrafts[key] !== undefined) return gradeDrafts[key];
    const existing = (data.grades || []).find(
      (g) =>
        String(g.studentId) === String(studentId) &&
        String(g.courseId) === String(courseId) &&
        String(g.examPointKey || '') === String(examPointKey)
    );
    if (!existing) {
      const fallbackExamType = String(examPointKey || '').split('-').slice(0, -1).join('-');
      const fallback = (data.grades || []).find(
        (g) =>
          String(g.studentId) === String(studentId) &&
          String(g.courseId) === String(courseId) &&
          String(g.examType || 'other') === String(fallbackExamType)
      );
      return fallback?.grade ?? '';
    }
    return existing?.grade ?? '';
  };

  const handleDraftChange = (studentId, courseId, examType, value) => {
    const key = `${studentId}-${courseId}-${examType}`;
    setGradeDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveGrade = async (studentId, courseId, examPoint, examPointKey) => {
    const examType = normalizeExamType(examPoint.name);
    const draftValue = getDraftValue(studentId, courseId, examPointKey);
    const numericGrade = Number(draftValue);

    if (Number.isNaN(numericGrade) || numericGrade < 0 || numericGrade > 10) {
      window.alert('Voer een geldig cijfer in tussen 0 en 10.');
      return;
    }

    const key = `${studentId}-${courseId}-${examPointKey}`;
    setSavingGradeKey(key);
    try {
      await upsertGrade({
        studentId,
        courseId,
        grade: numericGrade,
        examType,
        examPointKey,
        weight: Number(examPoint.weight || 0),
        notes: examPoint.name
      });
    } catch (error) {
      window.alert(error.message || 'Cijfer opslaan mislukt');
    } finally {
      setSavingGradeKey('');
    }
  };

  const handleExportPDF = () => {
    const course = selectedCourseDoc;
    if (!course) return;
    // Zelfde volgorde en naamweergave als op het scherm: "Familienaam Voornaam", gesorteerd op familienaam.
    const enrolledStudents = allEnrolledStudents.map((student) => ({ ...student, name: displayStudentName(student) }));
    exportCourseScoresReport(course, enrolledStudents, data.grades, `course_scores_${course.code}`);
  };

  const handleExportExcel = () => {
    const course = selectedCourseDoc;
    if (!course) return;
    // Zelfde volgorde en naamweergave als op het scherm: "Familienaam Voornaam", gesorteerd op familienaam.
    const enrolledStudents = allEnrolledStudents.map((student) => ({ ...student, name: displayStudentName(student) }));
    exportCourseScoresExcel(course, enrolledStudents, data.grades, `course_scores_${course.code}`);
  };

  // Enrolled students for the selected course
  const allEnrolledStudents = useMemo(() => {
    if (!selectedCourseDoc) return [];
    const byId = new Map();
    (data.students || []).forEach((student) => {
      byId.set(String(student.id || student._id), student);
    });

    (data.enrollments || [])
      .filter((enrollment) => String(enrollment.courseId) === String(selectedCourseDoc.id || selectedCourseDoc._id))
      .forEach((enrollment) => {
        const enrollmentStudentId = String(enrollment.studentId || enrollment.student?.id || enrollment.student?._id || enrollment.student || '');
        if (!enrollmentStudentId) return;

        if (!byId.has(enrollmentStudentId) && enrollment.student) {
          byId.set(enrollmentStudentId, {
            id: enrollmentStudentId,
            name: enrollment.student.name || '-',
            email: enrollment.student.email || '-'
          });
        }
      });

    (data.grades || [])
      .filter((grade) => String(grade.courseId) === String(selectedCourseDoc.id || selectedCourseDoc._id))
      .forEach((grade) => {
        const gradeStudentId = String(grade.studentId || grade.student?.id || grade.student?._id || grade.student || '');
        if (!gradeStudentId) return;

        if (!byId.has(gradeStudentId)) {
          byId.set(gradeStudentId, {
            id: gradeStudentId,
            name: grade.student?.name || `Student ${gradeStudentId}`,
            email: grade.student?.email || '-'
          });
        }
      });

    const orderedIds = [
      ...(data.enrollments || [])
        .filter((enrollment) => String(enrollment.courseId) === String(selectedCourseDoc.id || selectedCourseDoc._id))
        .map((enrollment) => String(enrollment.studentId || enrollment.student?.id || enrollment.student?._id || enrollment.student || '')),
      ...(data.grades || [])
        .filter((grade) => String(grade.courseId) === String(selectedCourseDoc.id || selectedCourseDoc._id))
        .map((grade) => String(grade.studentId || grade.student?.id || grade.student?._id || grade.student || ''))
    ].filter(Boolean);

    const seen = new Set();
    return orderedIds
      .filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((id) => byId.get(id))
      .filter(Boolean)
      .sort(compareByFamilyName);
  }, [data.enrollments, data.grades, data.students, selectedCourseDoc]);

  const searchLower = studentSearch.trim().toLowerCase();
  const enrolledStudents = searchLower
    ? allEnrolledStudents.filter(
        (s) =>
          `${displayStudentName(s)} ${s.name || ''} ${s.email || ''} ${getStudentCode(s)}`.toLowerCase().includes(searchLower)
      )
    : allEnrolledStudents;

  const visibleExamPoints = selectedExamPoint
    ? scoreExamPoints.filter((ep) => String(ep.pointKey) === String(selectedExamPoint))
    : scoreExamPoints;

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Cijfers invullen</h1>
      </div>

      <div className="report-controls">
        {/* Study filter */}
        <div className="form-group">
          <label>Studie</label>
          <select
            value={selectedStudy}
            onChange={(e) => {
              setSelectedStudy(e.target.value);
              setSelectedStudyYear('');
              setSelectedModule('');
              setSelectedCourse('');
              setSelectedExamPoint('');
              setStudentSearch('');
            }}
          >
            <option value="">-- Alle studies --</option>
            {[...studiesWithCourses]
              .sort((a, b) => getStudyOptionLabel(a).localeCompare(getStudyOptionLabel(b), 'nl'))
              .map((s) => (
              <option key={s.id || s._id} value={String(s.id || s._id)}>
                {getStudyOptionLabel(s)}
              </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label>{t('studyYear.title', 'Studiejaar')}</label>
          <select
            value={selectedStudyYear}
            onChange={(e) => {
              setSelectedStudyYear(e.target.value);
              setSelectedModule('');
              setSelectedCourse('');
              setSelectedExamPoint('');
              setStudentSearch('');
            }}
            disabled={!selectedStudy}
          >
            <option value="">-- Alle studiejaren --</option>
            {availableStudyYears.map((year) => (
              <option key={year.id || year._id} value={String(year.id || year._id)}>
                {year.name || `Studiejaar ${year.yearNumber || year.year || ''}`}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Module</label>
          <select
            value={selectedModule}
            onChange={(e) => {
              setSelectedModule(e.target.value);
              setSelectedCourse('');
              setSelectedExamPoint('');
              setStudentSearch('');
            }}
            disabled={!selectedStudy}
          >
            <option value="">-- Alle modules --</option>
            {availableModules.map((module) => (
              <option key={module.id || module._id} value={String(module.id || module._id)}>
                {module.code ? `${module.code} – ${module.name}` : module.name}
              </option>
            ))}
          </select>
        </div>

        {/* Course filter — shows only courses linked to selected study/module */}
        <div className="form-group">
          <label>{t('course.title')}</label>
          <select
            value={selectedCourse}
            onChange={(e) => {
              setSelectedCourse(e.target.value);
              setSelectedExamPoint('');
              setStudentSearch('');
            }}
          >
            <option value="">{t('common.select')}...</option>
            {filteredCoursesByStudy.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
          {selectedStudy && filteredCoursesByStudy.length === 0 && (
            <small className="hint-text">Geen vakken gekoppeld aan deze studie.</small>
          )}
        </div>

        {/* Exam point filter */}
        {selectedCourse && scoreExamPoints.length > 0 && (
          <div className="form-group">
            <label>Examenonderdeel</label>
            <select
              value={selectedExamPoint}
              onChange={(e) => setSelectedExamPoint(e.target.value)}
            >
              <option value="">-- Alle onderdelen --</option>
              {scoreExamPoints.map((ep, idx) => (
                <option key={ep.pointKey || idx} value={ep.pointKey}>
                  {ep.displayName || ep.name} ({ep.weight}%)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Student search */}
        {selectedCourse && (
          <div className="form-group">
            <label>Student zoeken</label>
            <input
              type="search"
              placeholder="Naam, e-mail of studentcode..."
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ccc' }}
            />
          </div>
        )}
      </div>

      <div className="export-buttons">
        <button
          className="btn-primary"
          onClick={handleExportPDF}
          disabled={!selectedCourse}
        >
          {t('report.exportPDF')}
        </button>
        <button
          className="btn-primary"
          onClick={handleExportExcel}
          disabled={!selectedCourse}
        >
          {t('report.exportExcel')}
        </button>
      </div>

      {/* Preview */}
      {selectedCourse && (
        <div className="report-preview">
          <h3>{t('grade.studentReport')}</h3>
          {allEnrolledStudents.length === 0 ? (
            <p>{t('common.noData')}</p>
          ) : (
            <div className="scores-table-container">
              {scoreExamPoints.length === 0 && (
                <p>Voor dit vak zijn nog geen examenonderdelen ingesteld.</p>
              )}
              {enrolledStudents.length === 0 && searchLower && (
                <p style={{ color: '#999', marginBottom: '10px' }}>
                  Geen studenten gevonden voor &ldquo;{studentSearch}&rdquo;.
                </p>
              )}
              {visibleExamPoints.length > 0 && enrolledStudents.length > 0 && (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('user.name')}</th>
                      <th>Studentcode</th>
                      <th>{t('user.email')}</th>
                      {visibleExamPoints.map((point, index) => (
                        <th key={`${point.pointKey || point.name}-${index}`}>
                          {point.displayName || point.name} ({point.weight}%)
                        </th>
                      ))}
                      {!selectedExamPoint && <th>Eindcijfer</th>}
                      {!selectedExamPoint && <th>{t('common.status')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {enrolledStudents.map((s) => {
                      const studentGrades = (data.grades || []).filter(
                        (g) => String(g.studentId) === String(s.id || s._id) && String(g.courseId) === String(selectedCourseDoc.id || selectedCourseDoc._id)
                      );
                      const weighted = calculateWeightedFinalGrade({
                        grades: studentGrades,
                        examPoints: scoreExamPoints
                      });
                      const finalGrade = weighted.finalGrade;
                      const flooredFinalGrade = floorToOneDecimal(finalGrade);
                      const passed = typeof flooredFinalGrade === 'number' && flooredFinalGrade >= 5.5;

                      return (
                        <tr key={s.id}>
                          <td>{displayStudentName(s)}</td>
                          <td>{getStudentCode(s) || '-'}</td>
                          <td>{s.email}</td>
                          {visibleExamPoints.map((examPoint, pointIdx) => {
                            const examType = examPoint.examType || normalizeExamType(examPoint.name || examPoint.displayName);
                                const examPointKey = String(examPoint.pointKey || examPoint._id || examPoint.id || `${examType}-${pointIdx}`);
                                const studentId = String(s.id || s._id);
                                const courseId = String(selectedCourseDoc.id || selectedCourseDoc._id);
                                const draftKey = `${studentId}-${courseId}-${examPointKey}`;
                            return (
                              <td key={`${s.id}-${pointIdx}`}>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    step="0.1"
                                        value={getDraftValue(studentId, courseId, examPointKey)}
                                    onChange={(e) =>
                                          handleDraftChange(studentId, courseId, examPointKey, e.target.value)
                                    }
                                    style={{ width: '78px' }}
                                  />
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                        onClick={() => handleSaveGrade(studentId, courseId, examPoint, examPointKey)}
                                    disabled={savingGradeKey === draftKey}
                                  >
                                    Opslaan
                                  </button>
                                </div>
                              </td>
                            );
                          })}
                          {!selectedExamPoint && (
                            <td>{typeof finalGrade === 'number' ? `${formatGradeOneDecimal(finalGrade)}/10` : '-'}</td>
                          )}
                          {!selectedExamPoint && (
                            <td>
                              <span
                                className={`status-badge ${passed ? 'status-passed' : 'status-failed'}`}
                              >
                                {passed ? t('grade.passed') : t('grade.failed')}
                              </span>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportGrades;
